import React from 'react';
import PropTypes from 'prop-types';
import './ExperienceBar.css';

/**
 * ExperienceBar component to display a Pokémon's progress toward the next level
 * 
 * @param {Object} props Component props
 * @param {number} props.totalExp Total experience points of the Pokémon
 * @param {boolean} props.showLevel Whether to show the level text (default: true)
 * @param {string} props.className Additional CSS classes
 * @param {Object} props.style Additional inline styles
 * @param {boolean} props.animate Whether to animate the bar when it updates (default: false)
 * @param {boolean} props.showValues Whether to show the numerical exp values (default: false)
 */
const ExperienceBar = ({ 
  totalExp, 
  showLevel = true, 
  className = '', 
  style = {}, 
  animate = false,
  showValues = false
}) => {
  // Calculate level based on total experience
  const getLevel = (exp) => {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  };

  // Calculate experience needed for next level
  const getExpForLevel = (level) => {
    return Math.pow(level, 3);
  };

  const currentLevel = getLevel(totalExp);
  const nextLevel = currentLevel + 1;
  
  // Calculate experience needed for current and next level
  const expForCurrentLevel = getExpForLevel(currentLevel);
  const expForNextLevel = getExpForLevel(nextLevel);
  
  // Calculate progress percentage toward next level
  const expProgress = totalExp - expForCurrentLevel;
  const expNeeded = expForNextLevel - expForCurrentLevel;
  const progressPercentage = Math.min(100, Math.max(0, (expProgress / expNeeded) * 100));

  return (
    <div className={`exp-bar-container ${className}`} style={style}>
      {showLevel && (
        <div className="exp-level-indicator">
          <span>Lv. {currentLevel}</span>
          <span className="exp-next-level">→ {nextLevel}</span>
        </div>
      )}
      
      <div className="exp-bar-wrapper">
        <div 
          className={`exp-bar-fill ${animate ? 'animate' : ''}`} 
          style={{ width: `${progressPercentage}%` }}
        />
        {showValues && (
          <div className="exp-values">
            <span>{expProgress} / {expNeeded}</span>
          </div>
        )}
      </div>
    </div>
  );
};

ExperienceBar.propTypes = {
  totalExp: PropTypes.number.isRequired,
  showLevel: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  animate: PropTypes.bool,
  showValues: PropTypes.bool
};

export default ExperienceBar;
